import { SeverityLevel } from '@microsoft/applicationinsights-common';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useParams } from 'react-router';
import { Col, Collapse, Nav, NavItem, NavLink, Row, Table, UncontrolledTooltip } from 'reactstrap';
import useGetCustomers from '../../requests/useGetCustomers';
import useGetOrders, { OrderDisplayDto } from '../../requests/useGetOrders';
import useOrderTransactionsByOrder from '../../requests/useOrderTransactionsByOrder';
import { customerDisplayName, customerDisplayNameFromDto } from '../../Utilities';
import BoldButton from '../Common/BoldButton';
import { CenteredSpinner } from '../Common/CenteredSpinner';
import { OptionalAmount } from '../Common/dtos/OptionalAmount';
import OrderDto from '../Common/dtos/OrderDto';
import OrderTransaction from '../Common/dtos/OrderTransactionDto';
import ProductDto from '../Common/dtos/ProductDto';
import RecipeDto from '../Common/dtos/RecipeDto';
import KaAlert from '../Common/KaAlert';
import { KaSelectOption, toKaSelectOptions } from '../Common/KaSelect';
import OverflowTooltip from '../Common/OverflowTooltip';
import FilterBadge from '../Common/Pagination/FilterBadge';
import PaginatedTable from '../Common/Pagination/PaginatedTable';
import QueryView from '../Common/QueryView';
import { toAbbreviation } from '../Common/Units';
import useDisplayMessage from '../Common/useDisplayMessage';
import FilterInput from '../FilterInput';
import { useLogException } from '../Monitoring/useMonitoring';
import { ItemType } from '../Orders/CreateEditOrderBlendItem';
import OrderSearchModal from '../Orders/OrderSearchModal';
import { OrderTransactionPage } from '../Orders/Transactions/OrderTransactionPage';
import useGetProducts from '../Requests/useGetProducts';
import useGetRecipes from '../Requests/useGetRecipes';
import useGetSites from '../Requests/useGetSites';
import { useKaEmployee } from '../UserAccess/useKaEmployee';
import { ViewOrder } from './ViewOrder';

export type FilterSettings = {
  item: string;
  customer: string;
  site: string;
  startDate: string;
  endDate: string;
}

type SortMode =
  { isHighlighted: boolean, field: SortField, direction: SortDirection }

enum SortField {
  number,
  createdAt,
  completedAt,
}

enum SortDirection {
  ascending,
  descending,
}

const defaultSort: SortMode = ({
  isHighlighted: false,
  field: SortField.number,
  direction: SortDirection.ascending
});

const AmountDisplay = ({ amount, hoverId }: { amount: OptionalAmount, hoverId: string }) => {
  if (amount.error) {
    return <span id={hoverId}>
      N/A
      <i className="bi-info-circle ms-2" />
      <UncontrolledTooltip placement="top" target={hoverId} >
        {amount.error}
      </UncontrolledTooltip>
    </span >
  }

  return <span>{amount.quantity?.toFixed(2) + ' ' + toAbbreviation(amount.unit!)}</span>;
}

const OrderTransactions = ({ orderId, requested, show }: { orderId: string, requested: OptionalAmount, show: boolean }) => {
  const [prevShow, setPrevShow] = useState(false);
  const getOrderTransactions = useOrderTransactionsByOrder(orderId, requested.unit ?? 'Pound', { autoQuery: false });

  const orderRemaining = (ots: OrderTransaction[]): OptionalAmount => {
    const error = requested.error
      ?? ots.find(ot => ot.totalDispensed.error)?.totalDispensed.error;

    if (error) return { error, unit: 'Pound' };

    const allDispensed = ots.map(ot => ot.totalDispensed.quantity ?? 0).reduce((sum, n) => sum + n, 0);

    return {
      quantity: (requested.quantity ?? 0) - allDispensed,
      unit: requested.unit,
    };
  }

  useEffect(() => {
    if (!prevShow && show) {
      getOrderTransactions.query();
    }

    setPrevShow(show);
  }, [show, getOrderTransactions, prevShow]);

  return <QueryView
    query={getOrderTransactions}
    renderLoading={() =>
      <td className="py-6 position-relative" colSpan={5}>
        <div className="position-absolute top-50 start-50 translate-middle">
          <CenteredSpinner />
        </div>
      </td>
    }
    renderData={ots =>
      ots.length === 0
        ? <td colSpan={7} className="justify-content-center">
          <h3 className="text-center">No Order Transactions</h3>
        </td>
        : <td colSpan={7}>
          <div>
            <Row className="mb-1">
              <Col xs={2}><span className="text-dark fw-semibold">Transactions</span></Col>
              <Col xs={2} />
              <Col xs={2} className="text-end"><span className="text-dark fw-semibold">Order Quantity</span></Col>
              <Col xs={2} className="text-end"><span className="text-dark fw-semibold">Dispensed Quantity</span></Col>
              <Col xs={2} className="text-end"><span className="text-dark fw-semibold">Completed Date</span></Col>
            </Row>
            {
              ots.sort((a, b) => (new Date(b.loadedAt).getTime() - new Date(a.loadedAt).getTime())).map(ot =>
                <Row key={ot.id} className="treeRow">
                  <Col xs={2} className="pb-2">{ot.number}</Col>
                  <Col xs={2}><span className="text-primary">Completed</span></Col>
                  <Col xs={2} className="text-end">
                    <AmountDisplay amount={requested} hoverId={`requested-${ot.id}`} />
                  </Col>
                  <Col xs={2} className="text-end">
                    <AmountDisplay amount={ot.totalDispensed} hoverId={`dispensed-${ot.id}`} />
                  </Col>
                  <Col xs={2} className="text-end">{new Date(ot.loadedAt).toLocaleString()}</Col>
                </Row>
              )
            }
          </div>
          <Row>
            <Col xs={9} />
            <Col xs={3}>
              <span className="text-primary fw-semibold">
                Order Remaining: <AmountDisplay amount={orderRemaining(ots)} hoverId={`total-${orderId}`} />
              </span>
            </Col>
          </Row>
        </td>
    }
  />
}

const OrderColumn = ({ children }: { children: JSX.Element }) =>
  <td className="py-2 align-middle">
    {children}
  </td>

const EmployeeOrderPage = () => {
  const [viewCompletedOrders, setViewCompletedOrders] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const [numberSearch, setNumberSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [sortMode, setSortMode] = useState(defaultSort);

  const [filters, setFilters] = useState<FilterSettings>({
    item: '',
    customer: '',
    site: '',
    startDate: '',
    endDate: '',
  });

  const updateFilters = (newFilters: Partial<FilterSettings>) =>
    setFilters({ ...filters, ...newFilters });

  const [viewOrder, setViewOrder] = useState<OrderDto | null>(null);

  const kaEmployee = useKaEmployee();
  const getSites = useGetSites();
  const getProducts = useGetProducts();
  const getRecipes = useGetRecipes();
  const getCustomers = useGetCustomers();

  const getOrders = useGetOrders({
    completed: viewCompletedOrders,
    page: currentPage,
    pageSize: pageSize,
    orderNumberFilter: debouncedSearch,
    sortField: SortField[sortMode.field],
    sortDirection: SortDirection[sortMode.direction],
    itemId: filters.item,
    customerId: filters.customer,
    siteId: filters.site,
    startDate: filters.startDate,
    endDate: filters.endDate,
  });

  const displayMessage = useDisplayMessage();
  const logException = useLogException();
  const { orderNumber: initialOrderNumber, completed: initialCompleted } = useParams();

  const [transactionOrderIds, setTransactionOrderIds] = useState<string[]>([]);

  const [showOrderTransactions, setShowOrderTransactions] = useState(false);
  const [orderForOrderTransactions, setOrderForOrderTransactions] = useState<OrderDisplayDto | undefined>(undefined);

  useEffect(() => {
    if (initialOrderNumber) {
      numberSearchChanged(initialOrderNumber);
    }

    if (initialCompleted === 'completed') {
      handleViewCompletedOrders(true);
    }
  }, [initialOrderNumber, initialCompleted]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(numberSearch);
    }, 600);

    return () => clearTimeout(handler);
  }, [numberSearch]);

  const numberSearchChanged = (number: string) => {
    setCurrentPage(0);
    setNumberSearch(number);
  };

  const handlePageChange = (selected: number) => {
    setCurrentPage(selected);
  };

  const handleViewCompletedOrders = (viewCompleted: boolean) => {
    setCurrentPage(0);

    if ((!viewCompleted && sortMode.field === SortField.completedAt) ||
      (viewCompleted && sortMode.field === SortField.createdAt)) {
      setSortMode({ ...defaultSort });
    }

    setViewCompletedOrders(viewCompleted);
  };

  const handleSortMode = (sortField: SortField) => {
    setSortMode((prev) => {
      const isSameFieldSelected = prev.isHighlighted && prev.field === sortField;

      return {
        isHighlighted: true,
        field: sortField,
        direction: isSameFieldSelected && prev.direction === SortDirection.ascending
          ? SortDirection.descending
          : SortDirection.ascending
      };
    });
  };

  const isSortColumnHighlighted = (sortField: SortField) =>
    sortMode.isHighlighted && sortField === sortMode.field;

  const getOrderItems = (order: OrderDto) => order.blends.flatMap(b => b.items.map(i => i.name))

  const viewClick = (order: OrderDisplayDto) => {
    displayMessage.clear();
    setViewOrder(order);
  }

  const showOrderTransactionClick = (order: OrderDisplayDto) => {
    setOrderForOrderTransactions(order);
    setShowOrderTransactions(true);
  }

  const setUnrecoverableDataError = (exception: Error, severityLevel?: SeverityLevel) => {
    setViewOrder(null);
    displayMessage.fail("Something is wrong with this order. Please contact Kahler Automation.");
    logException(exception, severityLevel);
  }

  const listOfItems = (products: ProductDto[], recipes: RecipeDto[]) => [
    ...products.map(p => ({
      value: p.id,
      label: p.name,
      type: ItemType.Product
    })),
    ...recipes.map(r => ({
      value: r.id,
      label: r.name,
      type: ItemType.Recipe
    }))
  ];

  const items = listOfItems(getProducts.data ?? [], getRecipes.data ?? []);
  const itemOptions: KaSelectOption[] = toKaSelectOptions<KaSelectOption & { type: ItemType }, KaSelectOption & { type: ItemType }>(
    items,
    "value",
    "label",
    { sortByLabel: true }
  );
  const filterItem = items.find((option) => option.value === filters.item);

  const customerOptions: KaSelectOption[] = toKaSelectOptions(getCustomers.data ?? [],
    "id",
    "name",
    {
      labelFn: customerDisplayNameFromDto,
      sortByLabel: true
    });
  const filterCustomer = customerOptions.find((option) => option.value === filters.customer);

  const siteOptions: KaSelectOption[] = toKaSelectOptions(getSites.data ?? [], "id", "name", { sortByLabel: true });
  const filterSite = siteOptions.find((option) => option.value === filters.site);

  const sortableColumnHeader = (name: string) => {
    const sortFieldMap: Record<string, SortField> = {
      Number: SortField.number,
      Created: SortField.createdAt,
      Completed: SortField.completedAt,
    };

    const sortField = sortFieldMap[name] ?? SortField.number;
    const isActive = isSortColumnHighlighted(sortField);
    const { direction } = sortMode;

    return <NavLink
      className="d-inline-flex align-items-center"
      active={isActive}
      onClick={() => handleSortMode(sortField)}>
      {name}
      <i className={`mx-1 ${isActive
        ? direction === SortDirection.ascending
          ? "bi-arrow-down"
          : "bi-arrow-up"
        : "bi-arrow-down-up"
        }`}
        style={{ fontSize: "0.75rem" }}
      />
    </NavLink>
  };

  const toggleTransactions = (orderId: string) => {
    const orderIdIndex = transactionOrderIds.indexOf(orderId);

    if (orderIdIndex === -1) {
      setTransactionOrderIds([...transactionOrderIds, orderId]);
    } else {
      const updatedIds = [...transactionOrderIds];
      updatedIds.splice(orderIdIndex, 1)
      setTransactionOrderIds(updatedIds);
    }
  }

  const createdOrCompleted = viewCompletedOrders ? 'Completed' : 'Created';

  return <div>
    {viewOrder !== null ? (
      <ViewOrder
        employee={kaEmployee}
        order={viewOrder!}
        onClose={() => setViewOrder(null)}
        setUnrecoverableDataError={setUnrecoverableDataError}
      />
    ) : showOrderTransactions && orderForOrderTransactions != null ? (
      <OrderTransactionPage
        profile={kaEmployee}
        order={orderForOrderTransactions}
        backClicked={() => setShowOrderTransactions(false)}
        viewOnly={true}
      />
    ) : (
      <>
        <Row className="justify-content-between mb-2">
          <h2 className="col-auto ka-blue">Orders</h2>
        </Row>
        <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
        <form>
          <div className="row gx-2 gx-md-3 mb-3">
            <div className="col-md-4 mb-2 mb-md-0">
              <FilterInput placeholder='Filter orders by number' value={numberSearch} onFilterTextChange={numberSearchChanged} />
            </div>
            <BoldButton className="col-auto btn-sm px-2 py-0"
              data-testid="show-search-modal"
              onClick={() => setShowSearchModal(true)}>
              <i className="bi-filter" style={{ fontSize: '2rem' }} />
            </BoldButton>
          </div>
        </form>
        <Row>
          <div className="col-auto">
            <FilterBadge
              label={filterItem?.type === ItemType.Product ? "Product" : "Recipe"}
              value={filterItem?.label}
              onClose={() => updateFilters({ item: '' })} />
            <FilterBadge
              label="Customer"
              value={filterCustomer?.label}
              onClose={() => updateFilters({ customer: '' })} />
            <FilterBadge
              label="Site"
              value={filterSite?.label}
              onClose={() => updateFilters({ site: '' })} />
            <FilterBadge
              label={createdOrCompleted}
              value={
                !!filters.startDate && !!filters.endDate
                  ? `${filters.startDate} - ${filters.endDate}`
                  : ''
              }
              onClose={() => updateFilters({ startDate: '', endDate: '' })} />
          </div>
        </Row>
        <div className="d-flex justify-content-center">
          <Nav tabs className="w-35">
            <NavItem className="w-50">
              <NavLink
                className="d-flex justify-content-center"
                active={!viewCompletedOrders}
                onClick={() => handleViewCompletedOrders(false)}>
                Active
              </NavLink>
            </NavItem>
            <NavItem className="w-50">
              <NavLink
                className="d-flex justify-content-center"
                active={viewCompletedOrders}
                onClick={() => handleViewCompletedOrders(true)}>
                Completed
              </NavLink>
            </NavItem>
          </Nav>
        </div>
        <QueryView query={getOrders}
          renderLoading={() => <div className="mt-8">
            <CenteredSpinner />
          </div>}
          renderData={paginatedOrders => {
            return <PaginatedTable
              onPageChange={handlePageChange}
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={paginatedOrders.totalPages}>
              <Table borderless className="mt-5">
                <thead>
                  <tr className="border-bottom border-dark border-1">
                    <th className="col-1" style={{ verticalAlign: 'top' }}>
                      {sortableColumnHeader("Number")}
                    </th>
                    <th className="col-2" style={{ verticalAlign: 'top' }}>Customer</th>
                    <th className="col-3" style={{ verticalAlign: 'top' }}>Products/Recipes</th>
                    <th className="col-2" style={{ verticalAlign: 'top' }}>Sites</th>
                    <th className="col-1" style={{ verticalAlign: 'top' }}>
                      {sortableColumnHeader(createdOrCompleted)}
                    </th>
                    <th className="col-1" style={{ verticalAlign: 'top' }} />
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.items
                    .map(order =>
                      <React.Fragment key={order.id}>
                        <tr className="border-top">
                          <OrderColumn>
                            <p className="mb-0">
                              {order.number}
                            </p>
                          </OrderColumn>
                          <OrderColumn>
                            <OverflowTooltip className="mb-0" text={customerDisplayName(order.customerName, order.customerAccountNumber)} />
                          </OrderColumn>
                          <OrderColumn>
                            <OverflowTooltip className="mb-0" text={getOrderItems(order).join(", ")} />
                          </OrderColumn>
                          <OrderColumn>
                            <OverflowTooltip className="mb-0" text={order.siteNames.join(", ")} />
                          </OrderColumn>
                          <OrderColumn>
                            <p className="mb-0">
                              {(() => {
                                const date = viewCompletedOrders ? order.completedAt : order.createdAt;
                                return date ? new Date(date).toLocaleDateString() : '';
                              })()}
                            </p>
                          </OrderColumn>
                          <OrderColumn>
                            <Row>
                              <Col xs={9} className="px-0">
                                <div className="dropdown">
                                  <Button
                                    className="dropdown-toggle btn-ghost-secondary"
                                    variant="link"
                                    id="actionsMenuButton"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                    style={{ marginTop: '3px' }}>
                                    <strong>Actions</strong>
                                  </Button>
                                  <div className="dropdown-menu" aria-labelledby="actionsMenuButton">
                                    <BoldButton className="dropdown-item btn-ghost-secondary" onClick={() => viewClick(order)}>
                                      View
                                    </BoldButton>
                                    {order.hasTransactions && <BoldButton className="dropdown-item btn-ghost-secondary" onClick={() => showOrderTransactionClick(order)}>View Transactions</BoldButton>}
                                  </div>
                                </div>
                              </Col>
                              <Col xs={3} className="px-0 d-block">
                                <button
                                  type="button"
                                  className="btn btn-icon btn-ghost-primary btn-link"
                                  data-testid={`showTransactions-${order.id}`}
                                  onClick={() => toggleTransactions(order.id)}>
                                  {<i className={transactionOrderIds.includes(order.id) ? 'bi-caret-up-square' : 'bi-caret-down-square'} />}
                                </button>
                              </Col>
                            </Row>
                          </OrderColumn>
                        </tr>
                        <Collapse isOpen={transactionOrderIds.includes(order.id)} tag={'tr'} className='border-start border-end'>
                          <OrderTransactions
                            orderId={order.id}
                            requested={order.totalRequested}
                            show={transactionOrderIds.includes(order.id)} />
                        </Collapse>
                      </React.Fragment>
                    )}
                </tbody>
              </Table>
            </PaginatedTable>
          }} />
        <OrderSearchModal
          show={showSearchModal}
          completed={viewCompletedOrders}
          itemOptions={itemOptions}
          customerOptions={customerOptions}
          siteOptions={siteOptions}
          filters={filters}
          updateFilters={updateFilters}
          onClose={() => setShowSearchModal(false)} />
      </>
    )}
  </div>;
}

export { EmployeeOrderPage };
